import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ClientAgentRunner} from '@rubii/sdk-web-openai/src/classes';
import { AgentSubmitToolOutput, ThreadStatus } from '@rubii/sdk-web-openai/src/types';
import { BaseChatCompletionMessage, BaseChatCompletionRole, ChatCompletionMessageToolCall } from '@rubii/sdk-web-openai/src/types/vendor-llm.type';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import hljs from 'highlight.js';
import { create } from 'domain';
import { BaseComponent, FileService, PusherService, UserService } from '@rubii/frontend-common';
import { AiAgent, AiMessage, AiThread, AiToolOutput } from '@rubii/entity/src/mdb-entities/ai';
import * as _ from 'lodash';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ExcelTabSelectorComponent } from './excel-tab-selector/excel-tab-selector.component';
import * as shared from '@rubii/share';
import * as XLSX from 'xlsx';
import { ImageModalComponent } from './image-modal/image-modal.component';
import { ClientService } from '../../../../services/client.service';
import { MarkdownService } from '../../../../services/markdown.service';
import { environment } from '../../../../../environments/environment';
import { UtilService } from '../../../../services/util.service';
import { Subscription } from 'rxjs';

interface ChatThread {
  id: number;
  name: string;
  messages?: BaseChatCompletionMessage[];
}

@Component({
  selector: 'app-ai-bot',
  templateUrl: './ai-bot.component.html',
  styleUrls: ['./ai-bot.component.scss']
})
export class AiBotComponent extends BaseComponent implements OnInit, OnDestroy {
  // Constants
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 2MB

  // Public properties
  threads: AiThread[] = [];
  selectedThread: AiThread = null;
  newMessage: string = '';
  waitingForAi: boolean = false;
  agents: AiAgent[];
  selectedAgent: AiAgent;
  agentRunner: ClientAgentRunner;
  streaming: boolean = true;
  isThreadsLoading: boolean = false;
  toolCalls: ChatCompletionMessageToolCall[] = [];
  jsonConcatenate: string = '';
  parsedMarkdownContent: string;
  uploadedFiles: Array<{ file: File, content: string, image: string, extraContent1: string, extraContent2: string }> = [];
  isDragging: boolean = false;
  @ViewChild('chatMessages') chatMessages: ElementRef;
  @ViewChild('messageInput') messageInput: ElementRef;
  @ViewChild('fileInputElement') fileInputElement: ElementRef;

  stopFunction: Function;

  templateKeyValues: Record<string, string> = {};
  showTemplateInjection: boolean = false;

  /**
   * Component constructor
   */
  constructor(
    private client: ClientService,
    private cdRef: ChangeDetectorRef,
    private pusher: PusherService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private snack: MatSnackBar,
    public markDownService: MarkdownService,
    private dialog: MatDialog,
    private fileService: FileService,
    private userService: UserService,
    private utilService: UtilService
  ) {
    super();
  }

  subscriptions: Subscription[] = [];

  /**
   * Angular lifecycle hook - component initialization
   */
  async ngOnInit() {
    window['aiBot'] = this;
    await this.getAgentList();
    // await this.initAgent('RubiiFaqAgent');
    // this.parsedMarkdownContent = this.markDownService.parse(this.testMarkdownContent) as string;

    this.subscriptions.push(this.route.queryParams.subscribe(async (queryParams) => {
      if (+queryParams.agent_id !== this.selectedAgent?._id) {
        const foundAgent = this.agents.find(agent => agent._id == queryParams.agent_id);
        if (foundAgent) {
          await this.selectAgent(foundAgent);
        }
      }
      if (+queryParams.thread_id !== this.selectedThread?._id) {
        const foundThread = this.threads.find(thread => thread._id == queryParams.thread_id);
        if (foundThread) {
          await this.selectThread(foundThread);
        }
      }
    }));
  }

  ngOnDestroy() {
    console.log('AI Bot - ngOnDestroy');
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  // Public methods used in template

  /**
   * Retrieves the list of available AI agents
   */
  async getAgentList() {
    const tempRunner = await this.client.ai.createAgentRunner('');
    this.agents = await tempRunner.listAvailableAgents();
    this.agents.sort((a, b) => new Date(b.updatedate).getTime() - new Date(a.updatedate).getTime());
  }

  /**
   * Retrieves the list of chat threads
   */
  async getThreadList() {
    this.isThreadsLoading = true;
    try {
      this.threads = ((await this.agentRunner.listThreads(30)) as AiThread[]);
      this.threads.sort((a, b) => new Date(b.createdate).getTime() - new Date(a.createdate).getTime());
    } finally {
      this.isThreadsLoading = false;
      this.cdRef.detectChanges();
    }
  }

  /**
   * Creates a new chat thread
   * @param manualClick - Whether the thread was created by manual user click
   */
  createNewThread(manualClick: boolean = false) {
    this.selectedThread = {
      id: null,
      name: 'New Thread',
      messages: [],
    } as any;

    if (manualClick) {
      this.utilService.removeUrlParamWithoutTriggeringNavigation ('thread_id');
    }
  }

  /**
   * Initializes an AI agent by name
   * @param name - Name of the agent to initialize
   */
  async initAgent(name: string) {
    this.selectedAgent = this.agents.find(agent => agent.name.replace(/\s+/g, '').toLowerCase() === name.replace(/\s+/g, '').toLowerCase());
    this.agentRunner = await this.client.ai.createAgentRunner(name);
    await this.agentRunner.load();
    await this.getThreadList();
    this.createNewThread();
  }

  /**
   * Selects an AI agent
   * @param agent - The agent to select
   */
  async selectAgent(agent: AiAgent) {
    this.selectedAgent = agent;
    await this.initAgent(agent.name);
    this.utilService.addUrlParamWithoutTriggeringNavigation ('agent_id', agent._id.toString());
  }

  /**
   * Selects a chat thread
   * @param thread - The thread to select
   */
  async selectThread(thread: AiThread) {
    if (!thread) {
      return;
    }
    this.selectedThread = await this.agentRunner.readThreadMessages(thread._id);
    if (this.selectedThread.messages) {
      this.selectedThread.messages = this.processMessagesWithTools(this.selectedThread.messages);
    }
    this.utilService.addUrlParamWithoutTriggeringNavigation ('thread_id', thread._id.toString());
  }

  /**
   * Sends a message in the current chat thread
   */
  async sendMessage() {
    if (this.waitingForAi || (!this.newMessage.trim() && this.uploadedFiles.length === 0)) {
      return;
    }

    let messageContent = this.newMessage.trim();
    this.stopFunction = null;
    
    const userMessage: BaseChatCompletionMessage = { role: BaseChatCompletionRole.User, content: messageContent || '' };

    // If there are uploaded files, add their info to the message
    if (this.uploadedFiles.length > 0) {
      const fileItem = this.uploadedFiles[0];
      if (fileItem.extraContent2?.match('(png|jpg|jpeg|gif)$')) {
        userMessage.image = fileItem.content; // base64
      } else if (fileItem.extraContent2?.match('(pdf|xlsx|csv)$')) {
        userMessage.content += '<media plan>' + fileItem.content + '</media plan>';
      }

      userMessage.extraContent1 = fileItem.extraContent1;
      userMessage.extraContent2 = fileItem.extraContent2;

      // if (messageContent) {
      //   messageContent += '\n\n';
      // }
      // messageContent += 'Attached files:\n';
      // this.uploadedFiles.forEach(file => {
      //   messageContent += `- ${file.file.name} (${file.file.type})\n`;
      // });
      
      // Reset uploaded files after sending
      this.uploadedFiles = [];
    }

    this.selectedThread.messages.push(userMessage as any);
    this.cdRef.detectChanges();
    setTimeout(() => {
      this.scrollToBottom();
      this.adjustTextareaHeight();
    }, 50);

    this.newMessage = '';
    this.waitingForAi = true;
    this.agentRunner.agentToolRunner = await this.client.ai.createToolRunner('');

    if (this.streaming) {
      // We can disable lambda URL now
      // if (!environment.debugging) {
      //   this.agentRunner.lambdaConnectoinId = Math.random().toString(36).substring(2, 15);
      // }

      this.jsonConcatenate = '';

      const newMessage = {
        _id: null,
        createdby: null,
        role: 'assistant',
        content: '',
      }
      this.selectedThread.messages.push(newMessage);
      console.log('templateKeyValues', this.templateKeyValues);

      try {
        await this.agentRunner.createThreadAndRunStreamFullCycle(
          [userMessage],
          null,
          this.selectedThread?._id,
          null,
          (chunk) => {
            newMessage.content += (chunk || '');
            this.clearToolLoading();
            this.cdRef.markForCheck();
            this.scrollToBottom();
          },
          this.handleToolOutput.bind(this),
          this.handleFinalMessage.bind(this),
          async (args, resolve, reject) => {
            console.log('Client Tool Args: ', args);
          },
          this.errorHandler.bind(this),
          (rejector: Function) => {
            this.stopFunction = rejector;
          }, {
            templateDict: this.templateKeyValues
          }
        );
      } catch (error) {
        this.waitingForAi = false;
      }
    } else {
      try {
        await this.agentRunner.createThreadAndRunFullCycle(
          [userMessage],
          null,
          this.selectedThread?._id,
          this.handleToolOutput.bind(this),
          this.handleFinalMessage.bind(this),
          this.errorHandler.bind(this),
          (rejector: Function) => {
            this.stopFunction = rejector;
          },
          {
            templateDict: this.templateKeyValues
          }
        );
      } catch (error) {
        this.waitingForAi = false;
      }
    }
  }

  /**
   * Stops the current AI generation
   */
  stopGeneration() {
    console.log('stopGeneration');
    this.stopFunction && this.stopFunction();
    // this.waitingForAi = false;
    // this.cdRef.detectChanges();
  }

  /**
   * Clears the loading state of tool messages
   */
  clearToolLoading() {
    this.selectedThread.messages.filter(m => m.role === 'tool').forEach(m => (m as any).isLoading = false);
  }

  /**
   * Scrolls the chat window to the bottom
   */
  scrollToBottom() {
    this.chatMessages.nativeElement.scrollTop = this.chatMessages.nativeElement.scrollHeight;
  }

  /**
   * Deletes a chat thread
   * @param threadId - ID of the thread to delete
   * @param event - Click event
   */
  async deleteThread(threadId: number, event: Event) {
    event.stopPropagation();
    this.isThreadsLoading = true;
    try {
      await this.agentRunner.deleteThread(threadId);
      await this.getThreadList();
    } finally {
      this.isThreadsLoading = false;
      this.cdRef.detectChanges();
    }
  }

  /**
   * Handles keyboard events in the message input
   * @param event - Keyboard event
   */
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
    setTimeout(() => this.adjustTextareaHeight());
  }

  /**
   * Copies message content to clipboard
   * @param content - Message content to copy
   */
  copyMessageToClipboard(content: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    console.log('Message copied to clipboard');
  }

  /**
   * Determines if monospace font should be used for content
   * @param content - Content to check
   */
  shouldUseMonospace(content: string): boolean {
    if (content.length > 1000) return true;
    const specialCharsPattern = /[\|\-\+\=]{5,}/;
    return specialCharsPattern.test(content);
  }

  /**
   * Finds tool output by ID in a message
   * @param toolId - Tool ID to find
   * @param message - Message to search in
   */
  findToolOutput(toolId: string, message: any): string {
    const found = message.toolOutputs?.find(t => t.toolCallId === toolId);
    return found?.output;
  }

  /**
   * Processes file input from input element
   * @param event - File input change event
   */
  handleFileInput(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (element.files && element.files.length > 0) {
      for (let i = 0; i < element.files.length; i++) {
        this.fileUpload(element.files[i]);
      }
      // Reset file input
      element.value = '';
    }
  }

  /**
   * Gets appropriate icon for file type
   * @param fileType - MIME type of the file
   */
  getFileIcon(fileType: string): string {
    if (fileType.includes('pdf')) {
      return 'picture_as_pdf';
    } else if (fileType.includes('sheet') || fileType.includes('excel') || fileType.includes('csv')) {
      return 'table_chart';
    } else if (fileType.includes('image')) {
      return 'image';
    } else {
      return 'insert_drive_file';
    }
  }

  /**
   * Removes file at specified index
   * @param index - Index of file to remove
   */
  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  /**
   * Handles file drop event
   * @param event - Drop event
   */
  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    
    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        const file = event.dataTransfer.files[i];
        this.fileUpload(file);
      }
    }
  }

  /**
   * Handles drag over event
   * @param event - Drag over event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Handles drag enter event
   * @param event - Drag enter event
   */
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  /**
   * Handles drag leave event
   * @param event - Drag leave event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  /**
   * Handles paste event for clipboard images
   * @param event - Paste event
   */
  onPaste(event: ClipboardEvent): void {
    if (event.clipboardData && event.clipboardData.items) {
      for (let i = 0; i < event.clipboardData.items.length; i++) {
        const item = event.clipboardData.items[i];
        if (item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();
          if (blob) {
            // Create a File object from the Blob
            const randomPngName = `clipboard-${Date.now()}.png`;
            const file = new File([blob], randomPngName, { type: blob.type });
            this.fileUpload(file);
            event.preventDefault();
            break;
          }
        }
      }
    }
  }

  /**
   * Processes uploaded file
   * @param file - File to upload
   */
  async fileUpload(file: File) {
    // Check if a file is already uploaded
    if (this.uploadedFiles.length > 0) {
      this.snack.open('Only one file can be uploaded at a time. Please remove the existing file first.', 'Dismiss', { duration: 5000 });
      return;
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      this.snack.open(`File size exceeds the 2MB limit. File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`, 'Dismiss', { duration: 5000 });
      return;
    }

    // Check if file type is allowed
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];
    
    // Also check by file extension for cases where MIME type might not be accurate
    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['.pdf', '.xlsx', '.xls', '.csv', '.jpg', '.jpeg', '.png', '.gif'];
    const hasAllowedExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!allowedTypes.some(type => file.type.includes(type.split('/')[1])) && 
        !allowedTypes.some(type => file.type === type) && 
        !hasAllowedExtension) {
      this.snack.open(`File type not allowed: ${file.type || fileName}`, 'Dismiss', { duration: 3000 });
      return;
    }

    if (file.type.match(/csv$/)) {
      // read in the file as a string
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        this.uploadedFiles.push({
          file: file,
          content: text,
          image: null,
          extraContent1: null,
          extraContent2: '.csv'
        });
        console.log(text);
      };
      reader.readAsText(file);
      return;
    }

    let xlsxTabName: string;
    if (file.name.match(/xlsx$/)) {
      const { sheetNames, workbook } = await this.getWorkSheetAndTab(file);

      if ( sheetNames.length === 1 ) {
        xlsxTabName = sheetNames[0];
      } else {
        const dialogRef = this.dialog.open(ExcelTabSelectorComponent, {
          width: '400px',
          data: { tabs: sheetNames }
        });

        try {
          xlsxTabName = await new Promise((resolve) => {
            dialogRef.afterClosed().subscribe(async selectedTab => {
              resolve(selectedTab);
            });
          });
        } catch (error) {
        }

        if (!xlsxTabName) {
          this.snack.open('No tab selected', 'Dismiss', { duration: 3000 });
          return;
        }
      }

      console.log(`xlsxTabName: ${xlsxTabName}`);
      // const csvData = this.convertToCSV(workbook, xlsxTabName);
      // console.log(`csvData: ${csvData}`);
      // return;
    }
    
    const prefix = 'ai-agent-files';
    const agentName = this.selectedAgent.name;
    const threadId = this.selectedThread?._id || 10000;
    const tenantId = this.userService.user.tenant.id;
    const s3Key = `file/${tenantId}/${prefix}/${agentName}/${threadId}/${file.name}`;

    // upload the file to s3
    try {
      const preSignedUrl = await this.client.ai.fileUploadPresign(s3Key, file.type);
      console.log(`===> preSignedUrl:`);
      console.log(preSignedUrl);

      // use fetch to upload the file to s3
      const response = await fetch(preSignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      if (!response.ok) {
        this.snack.open('Error uploading file', 'Dismiss', { duration: 3000 });
        return;
      }
    } catch (error: any) {
      console.error(error);
      this.snack.open('Error uploading file', 'Dismiss', { duration: 3000 });
    }

    // convert the file to the appropriate format
    const result = await this.client.ai.fileConvert({
      agentName: this.selectedAgent.name,
      threadId,
      outputFormat: 'csv',
      s3Key,
      tabName: xlsxTabName
    });

    console.log(result);
    console.log('File uploaded', file);

    this.uploadedFiles.push({
      file: file,
      content: result.content,
      image: result.image,
      extraContent1: result.extraContent1,
      extraContent2: result.extraContent2
    });

    
    // Focus textarea after upload
    setTimeout(() => {
      this.messageInput.nativeElement.focus();
    }, 300);
  }

  // Private methods
  private async getWorkSheetAndTab(file: File) {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, {
      cellStyles: true,
      cellDates: true,
      cellNF: true,
      sheetStubs: true
    });
    const visibleSheetNames = workbook.SheetNames.filter(name => {  
      const sheetProps = workbook.Workbook.Sheets.find(s => s.name === name);
  
      // Check if the sheet is hidden
      // 0 or undefined = visible, 1 = hidden, 2 = very hidden
      const isHidden = sheetProps && (sheetProps.Hidden === 1 || sheetProps.Hidden === 2);
      return !isHidden;
    });
    return {
      workbook,
      sheetNames: visibleSheetNames
    }
  }

  /**
   * Adjusts the height of the message input textarea
   */
  private adjustTextareaHeight() {
    const textarea = this.messageInput.nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  /**
   * Handles tool output during message processing
   */
  private async handleToolOutput(flag, threadId, toolCallMessage, toolCall, toolOutput: { toolCallId: string, output: string }) {
    if (flag === 'start') {
      this.selectedThread = await this.agentRunner.readThreadMessages(threadId);
      this.selectedThread.messages = this.processMessagesWithTools(this.selectedThread.messages);
    } else if (flag === 'partial') {
      const toolId = toolOutput.toolCallId;
      toolOutput.output = JSON.stringify(toolOutput.output);
      this.selectedThread.messages.filter(m => m.toolCalls?.length > 0).forEach(m => {
        if (m.toolCalls.find(t => t._id === toolId)) {
          m.toolOutputs.push(toolOutput as AiToolOutput);
        }
      });
    }

    this.cdRef.markForCheck();
    this.scrollToBottom();
  }

  /**
   * Handles the final message in a conversation
   */
  private async handleFinalMessage(threadId, finalMessage) {
    this.selectedThread._id = threadId;
    await this.selectThread(this.selectedThread);
    await this.getThreadList();
    this.waitingForAi = false;
  }

  private errorHandler(error: any) {
    console.error(error);
    this.waitingForAi = false;
    this.openSnackBarWarning(this.snack, error, 'error');
  }

  /**
   * Processes messages with tool calls and outputs
   * @param messages - Messages to process
   */
  private processMessagesWithTools(messages: AiMessage[]): any[] {
    const resultMessages: any[] = [];

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];

      if (message.toolCalls?.length > 0 && !message.toolOutputs?.length) {
        message.toolOutputs = [];
        for (let j = i + 1; j < messages.length; j++) {
          const nextMessage = messages[j];
          if (nextMessage.toolOutputs?.length > 0) {
            message.toolOutputs.push(...nextMessage.toolOutputs.filter((t: any) => message.toolCalls.some((o: any) => o._id === t.toolCallId)));
            break;
          }
        }

        if (!message.toolOutputs?.length) {
          (message as any).isLoading = true;
        }

        if (message.role !== 'tool') {
          const duplicateToolMessage: AiMessage = _.cloneDeep(message);
          duplicateToolMessage.role = 'tool';
          duplicateToolMessage.content = '';
          resultMessages.push(message);
          resultMessages.push(duplicateToolMessage);
          continue;
        }
      }
      resultMessages.push(message);
    }

    return resultMessages;
  }

  // Testing/Debug functions and commented out variables
  /*
  private async testStream() {
    const connectionId = '1234567890';
    console.log('Testing stream');
    this.pusher.bind(connectionId, 'hello', (data) => {
      console.log('Received data', data);
    });
    this.client.tool.testStream(connectionId);
  }
  */

  openImageModal(imageUrl: string): void {
    this.dialog.open(ImageModalComponent, {
      data: { imageUrl },
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      panelClass: 'full-screen-modal'
    });
  }

  convertToCSV(workbook: any, sheetName: string) {
    if (!workbook || !sheetName) {
      console.error('Please upload a file first');
      return;
    }

    try {
      // Get the specific worksheet
      const worksheet = workbook.Sheets[sheetName];
      
      // Convert to CSV
      const csvData = XLSX.utils.sheet_to_csv(worksheet);
      return csvData;
    } catch (error) {
      console.error('Error converting to CSV:', error);
    }
  }

  /**
   * Toggles the template injection panel
   */
  toggleTemplateInjection() {
    this.showTemplateInjection = !this.showTemplateInjection;
    
    // Initialize template key values if showing and agent has template keys
    if (this.showTemplateInjection && this.selectedAgent?.templateKeys) {
      const keys = this.selectedAgent.templateKeys as string[];
      keys.forEach(key => {
        if (!this.templateKeyValues[key]) {
          this.templateKeyValues[key] = '';
        }
      });
    }
  }

  /**
   * Updates a template key value
   */
  updateTemplateKeyValue(key: string, value: string) {
    this.templateKeyValues[key] = value;
  }

  /**
   * Checks if selected agent has template keys
   */
  hasTemplateKeys(): boolean {
    return this.selectedAgent?.templateKeys && (this.selectedAgent.templateKeys as string[]).length > 0;
  }
}
